import { useState } from "react";
import { ArrowRight, Menu, X } from "lucide-react";
import { NavLink } from "react-router-dom";
import { logoUrl, pageNavLinks } from "@/components/equinox/data";

export default function SiteHeader({ ctaLabel, onPrimaryAction }) {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <>
      <header className="header-shell sticky top-0 z-30 px-6 py-4 sm:px-8 lg:px-12" data-testid="site-header-shell">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4">
          <NavLink to="/" className="group flex items-center gap-3" data-testid="nav-logo-link">
            <div className="relative">
              <div className="absolute inset-0 rounded-2xl bg-blue-500/10 blur-xl transition-opacity duration-300 group-hover:opacity-100" />
              <img src={logoUrl} alt="Equinox logo" className="relative h-11 w-11 rounded-2xl object-cover ring-1 ring-white/10 transition-transform duration-300 group-hover:scale-[1.03]" data-testid="nav-logo-image" />
            </div>
            <div>
              <p className="font-display text-base font-semibold tracking-[0.14em] text-white sm:text-lg">EQUINOX MARKETING</p>
              <p className="text-[11px] uppercase tracking-[0.28em] text-white/[0.45]">App Growth Systems</p>
            </div>
          </NavLink>

          <nav className="hidden items-center gap-6 lg:flex" data-testid="desktop-nav-links">
            {pageNavLinks.map((link) => (
              <NavLink
                key={link.href}
                to={link.href}
                className={({ isActive }) => `rounded-full border px-3 py-2 text-sm uppercase tracking-[0.22em] transition-colors duration-300 ${isActive ? "border-white/[0.14] bg-white/[0.07] text-white shadow-[0_0_0_1px_rgba(255,255,255,0.03)]" : "border-transparent text-white/[0.58] hover:border-white/[0.08] hover:bg-white/[0.04] hover:text-white"}`}
                data-testid={`desktop-nav-link-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onPrimaryAction}
              className="glass-button hidden rounded-full bg-white px-5 py-3 text-sm font-medium text-black transition-transform duration-300 hover:scale-105 md:block"
              data-testid="nav-primary-cta-button"
            >
              <span className="relative z-10 inline-flex items-center gap-2">
                {ctaLabel}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </button>

            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              className="glass-button rounded-full border border-white/[0.12] bg-white/[0.04] p-3 text-white transition-transform duration-300 hover:scale-105"
              data-testid="nav-menu-button"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      {menuOpen && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-[#030408]/94 backdrop-blur-2xl" data-testid="menu-overlay">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(37,99,235,0.12),transparent_28%),linear-gradient(180deg,rgba(255,255,255,0.03),transparent_18%)]" />
          <button
            type="button"
            onClick={() => setMenuOpen(false)}
            className="absolute right-6 top-6 rounded-full border border-white/[0.12] bg-white/[0.05] p-3 text-white transition-transform duration-300 hover:scale-105"
            data-testid="menu-close-button"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="relative z-10 flex flex-col items-center gap-6 text-center">
            {pageNavLinks.map((link) => (
              <NavLink
                key={link.href}
                to={link.href}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) => `font-display text-3xl transition-transform duration-300 hover:scale-105 sm:text-5xl ${isActive ? "text-white" : "text-white/78 hover:text-white"}`}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {link.label}
              </NavLink>
            ))}

            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                onPrimaryAction();
              }}
              className="glass-button mt-6 rounded-full bg-white px-6 py-4 text-sm font-medium text-black transition-transform duration-300 hover:scale-[1.03]"
              data-testid="menu-primary-cta-button"
            >
              <span className="relative z-10 inline-flex items-center gap-2">
                {ctaLabel}
                <ArrowRight className="h-4 w-4" />
              </span>
            </button>
          </div>
        </div>
      )}
    </>
  );
}